/**
 * Shared fan/follow relationship store.
 *
 * Several components on a single profile render the same fan/follow data:
 * the Follow/Fan buttons, the compact counts, and the profile Connections card.
 * Without a shared store, clicking "Follow" only updated the button that was
 * clicked and the counts stayed stale until a page refresh. This tiny pub/sub
 * cache keeps every subscriber for a given `target_type:target_id` in sync the
 * instant a relationship is toggled.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const cache = new Map(); // key -> status { fan, follow, fan_count, follow_count }
const subs = new Map(); // key -> Set(fn)
const inflight = new Map(); // key -> Promise

const keyOf = (t, id) => `${t}:${id}`;

function notify(key) {
  const set = subs.get(key);
  if (set) set.forEach((fn) => { try { fn(cache.get(key)); } catch (e) { /* ignore */ } });
}

export function getCachedStatus(targetType, targetId) {
  return cache.get(keyOf(targetType, targetId)) || null;
}

export function subscribeStatus(targetType, targetId, fn) {
  const key = keyOf(targetType, targetId);
  if (!subs.has(key)) subs.set(key, new Set());
  subs.get(key).add(fn);
  return () => { const set = subs.get(key); if (set) set.delete(fn); };
}

export async function fetchStatus(targetType, targetId) {
  const key = keyOf(targetType, targetId);
  if (inflight.has(key)) return inflight.get(key);
  const p = api
    .get(`/network/relationship/status`, { params: { target_type: targetType, target_id: targetId } })
    .then(({ data }) => { cache.set(key, data); inflight.delete(key); notify(key); return data; })
    .catch((e) => { inflight.delete(key); throw e; });
  inflight.set(key, p);
  return p;
}

export async function toggleRelationship(targetType, targetId, kind) {
  const key = keyOf(targetType, targetId);
  const { data } = await api.post(`/network/relationship`, { target_type: targetType, target_id: targetId, kind });
  cache.set(key, data);
  notify(key);
  return data;
}

/** Subscribe a component to the live status for a profile. Returns the status or null. */
export function useRelationshipStatus(targetType, targetId) {
  const [st, setSt] = useState(() => getCachedStatus(targetType, targetId));
  useEffect(() => {
    if (!targetType || !targetId) return undefined;
    setSt(getCachedStatus(targetType, targetId));
    const unsub = subscribeStatus(targetType, targetId, setSt);
    fetchStatus(targetType, targetId).catch(() => {});
    return unsub;
  }, [targetType, targetId]);
  return st;
}
